// Shared UI primitives for the farm-dashboard app, consolidated from the
// near-duplicate Input/Select/Button/Pill/modal-shell implementations that
// used to be redefined locally in nearly every page file. Import from here
// instead of hand-rolling another copy.
//
// Looks come from classes in ./ui.css so hover and focus states work; a
// `style` prop passed by a caller is still applied on top and wins.

import ReactDOM from 'react-dom';
import './ui.css';

const cx = (...parts) => parts.filter(Boolean).join(' ');

// ── Input / Select ───────────────────────────────────────────────────────────
export const Input = ({ invalid, className, ...props }) => (
  <input {...props} className={cx('ui-input', invalid && 'is-invalid', className)} />
);

export const Textarea = ({ invalid, className, ...props }) => (
  <textarea {...props} className={cx('ui-input', invalid && 'is-invalid', className)} />
);

export const Select = ({ className, ...props }) => (
  <select {...props} className={cx('ui-input', className)} />
);

// ── Field (label + control + hint row) ───────────────────────────────────────
export const Field = ({ label, children, hint, style }) => (
  <div style={{
    display: 'grid', gridTemplateColumns: 'minmax(180px, 240px) 1fr', gap: 12, alignItems: 'center',
    ...style,
  }}>
    <div style={{ fontWeight: 600 }}>{label}</div>
    <div>
      {children}
      {hint && <div style={{ marginTop: 6, color: 'var(--muted)' }}><small>{hint}</small></div>}
    </div>
  </div>
);

export const Checkbox = ({ checked, onChange, label, style }) => (
  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, ...style }}>
    <input type="checkbox" checked={!!checked} onChange={onChange} style={{ transform: 'scale(1.15)' }} />
    <span>{label}</span>
  </label>
);

// ── Button ────────────────────────────────────────────────────────────────────
// variants: default | primary | dark | danger | success | income | expense |
// ghost | muted. size="sm" for dense rows.
export const Button = ({ children, variant = 'default', size, className, type = 'button', ...props }) => (
  <button
    type={type}
    {...props}
    className={cx('ui-btn', variant !== 'default' && `ui-btn--${variant}`, size === 'sm' && 'ui-btn--sm', className)}
  >
    {children}
  </button>
);

// ── Pill / Badge ──────────────────────────────────────────────────────────────
// Non-interactive status tags keep the true-pill (999px) shape, matching the
// site-wide convention of flattening buttons but leaving badges/tags round
// (see farmDashboard.css .stage-pill, .id-pill, .flood-inline .pill).
//
// tone gives a semantic look (ok/warn/bad/blue/income/expense); bg/fg is an
// escape hatch for call sites that pick raw colors directly (e.g. per-type
// lookup maps) rather than a fixed tone vocabulary.
export const Pill = ({ children, tone, bg, fg, style }) => (
  <span
    className={cx('ui-pill', tone && `ui-pill--${tone}`)}
    style={{
      ...(bg ? { background: bg } : {}),
      ...(fg ? { color: fg } : {}),
      ...style,
    }}
  >
    {children}
  </span>
);

// ── Page header ───────────────────────────────────────────────────────────────
// One header for every page: serif title (matching the nav label), optional
// subtitle and inline meta (e.g. a live badge), actions on the right, and the
// page's tabs underneath.
export function PageHeader({ title, subtitle, meta, actions, tabs, activeTab, onTabChange, children }) {
  return (
    <header className="ui-page-header">
      <div className="ui-page-header__row">
        <div className="ui-page-header__titles">
          <div className="ui-page-header__title-line">
            <h1 className="ui-page-title">{title}</h1>
            {meta}
          </div>
          {subtitle && <p className="ui-page-subtitle">{subtitle}</p>}
        </div>
        {actions && <div className="ui-page-actions">{actions}</div>}
      </div>
      {tabs && <Tabs tabs={tabs} value={activeTab} onChange={onTabChange} />}
      {children}
    </header>
  );
}

// tabs: [{ key, label, icon?, count? } | { divider: true }]
export function Tabs({ tabs, value, onChange }) {
  return (
    <div className="ui-tabs" role="tablist">
      {tabs.map((t, i) => (t.divider ? (
        <span key={`divider-${i}`} className="ui-tabs__divider" aria-hidden="true" />
      ) : (
        <button
          key={t.key}
          type="button"
          role="tab"
          aria-selected={value === t.key}
          className={cx('ui-tab', value === t.key && 'is-active')}
          onClick={() => onChange(t.key)}
          title={t.title}
        >
          {t.icon && <span className="ui-tab__icon" aria-hidden="true">{t.icon}</span>}
          {t.label}
          {t.count > 0 && <span className="ui-tab__count">{t.count}</span>}
        </button>
      )))}
    </div>
  );
}

// ── Stat tile ─────────────────────────────────────────────────────────────────
// accent colours the left edge; valueColor the number itself.
export const Stat = ({ label, value, sub, accent, valueColor, style }) => (
  <div
    className="ui-stat"
    style={{
      ...(accent ? { '--stat-accent': accent } : {}),
      ...(valueColor ? { '--stat-value': valueColor } : {}),
      ...style,
    }}
  >
    <div className="ui-stat__label">{label}</div>
    <div className="ui-stat__value">{value}</div>
    {sub && <div className="ui-stat__sub">{sub}</div>}
  </div>
);

export const StatRow = ({ children, style }) => (
  <div className="ui-stat-row" style={style}>{children}</div>
);

// ── Modal shell (backdrop + centered panel) ──────────────────────────────────
// Rendered via a portal straight into document.body: `position: fixed` only
// pins to the viewport when every ancestor is transform-free, and this shell
// gets used from deep inside animated (transform-using) dashboard panels -
// without the portal it ends up fixed relative to that panel instead, which
// on a long page can land the modal way off-screen until you scroll for it.
export function ModalShell({ children, onClose, width = 540, maxWidth = '94vw', zIndex = 1000, dark = false, ariaLabel, backdropStyle, panelStyle }) {
  return ReactDOM.createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex,
        background: dark ? 'rgba(0,0,0,0.55)' : 'rgba(10,28,40,0.42)',
        backdropFilter: 'blur(3px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
        ...backdropStyle,
      }}
    >
      <div
        role="dialog" aria-modal="true" aria-label={ariaLabel}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: dark ? '#1e2a35' : 'var(--panel)',
          color: dark ? '#fff' : 'var(--text)',
          border: dark ? 'none' : '1px solid var(--border)',
          borderRadius: 'var(--r-2xl)',
          boxShadow: 'var(--shadow-pop)',
          width: typeof width === 'number' ? `${width}px` : width,
          maxWidth,
          maxHeight: '90vh', overflowY: 'auto',
          padding: '24px 24px 20px',
          ...panelStyle,
        }}
      >
        {children}
      </div>
    </div>,
    document.body
  );
}

// ── Card (generic panel wrapper) ─────────────────────────────────────────────
// Forwards extra props (onClick, role, etc.) like Button/Input/Select
// already do - otherwise a `<Card onClick={...}>` silently does nothing,
// since the click handler never reaches the underlying element.
export const Card = ({ children, className, ...props }) => (
  <div {...props} className={cx('ui-card', className)}>
    {children}
  </div>
);
